export type SseEventType =
  | 'HPM_CONTACT_PROFILE'
  | 'HPM_MESSAGE'
  | 'TECH_STACK'
  | 'AI_SIGNAL'
  | 'CUSTOM_AI_SIGNAL'
  | 'ASK_AI'
  | 'CONTACT_ENRICHMENT'
  | 'CONTACT_ENRICHMENT_V2'
  | 'EMAIL_VALIDATION'
  | 'COMPANY_ENRICHMENT'
  // | 'DEPT_HEADCOUNT'
  // | 'COMPANY_HEADCOUNT'
  | 'CONTACT_REFRESH'
  | 'CONTACT_REFRESH_LOGS'
  | 'CONTACT_ENRICH_LOGS'
  | 'COMPANY_ENRICH_LOGS'
  | 'MOVE_TO_TAL'
  | 'MDQS_JOB'
  | 'MOVE_TO_TAL_LOGS'
  | 'IMPORT_LOGS'
  | 'INTEGRATED_DATA'
  | 'AI_HPM_CONFIG'
  | 'AI_INTENT'
  | 'HPM_FILE_CONTENT_LIST'
  | 'CUSTOM_ICP_SCORING'
  | 'CUSTOM_AI_SIGNAL_ACCOUNT'
  | 'ASK_AI_ACCOUNT_CONTACT'
  | 'ASK_AI_POLLING'
  | 'ICP_TRIGGER_STATE'
  | 'ENGAGEMENT_SCORE';

export interface SsePayload {
  event: string;
  jobId: string;
  status: string;
  message: string | null;
  data: Record<string, unknown> | Array<Record<string, unknown>> | null;
}

const TERMINAL_STATUSES = [
  'COMPLETED', 'FAILED', 'SUCCESS', 'ERROR',
  'DONE', 'TERMINATED', 'MOVED', 'CANCELLED',
  'NOT_REVEALED',
];

export function isTerminal(status: string | null | undefined): boolean {
  if (!status) return false;
  return TERMINAL_STATUSES.includes(status.toUpperCase());
}

export interface SseStreamOptions {
  headers: Record<string, string>;
  onError?: (err: Error) => void;
  maxRetries?: number;
  baseDelay?: number;
  onRetry?: (info: { attempt: number; maxRetries: number; delay: number; error: Error }) => void;
}

/**
 * Opens an SSE stream via fetch() with custom headers and automatic retry.
 * Returns a close function — call on unmount to abort the stream.
 *
 * Retry rules:
 *  - Does NOT retry on intentional close (component unmount)
 *  - Does NOT retry on 401/403 (auth error)
 *  - Does NOT retry after a terminal status was received
 *  - DOES retry on network drops, 5xx, unexpected stream close
 *  - Exponential backoff with jitter: delay = baseDelay × 2^attempt ± random(500ms)
 */
export function openSseStream(
  url: string,
  onEvent: (payload: SsePayload) => void,
  options: SseStreamOptions,
): () => void {
  const { headers, onError = console.error, maxRetries = 3, baseDelay = 2000, onRetry } = options;

  const controller = new AbortController();
  let attempts = 0;
  let terminated = false;
  let closed = false;

  function connect() {
    fetch(url, { headers, signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          const permanent = res.status === 401 || res.status === 403;
          handleError(new Error(`SSE connect failed: HTTP ${res.status}`), permanent);
          return;
        }

        attempts = 0;

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        function pump() {
          reader.read()
            .then(({ done, value }) => {
              if (done) {
                if (!closed) {
                  // Server closed the connection. Synthesize a job-level COMPLETED
                  // (data: null) so callers can clean up. This fires even when
                  // per-item COMPLETED events were already received (terminated=true),
                  // because those don't signal that the whole job is done — only the
                  // server closing the connection does.
                  terminated = true;
                  onEvent({ event: '', jobId: '', status: 'COMPLETED', message: null, data: null });
                }
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';

              for (const line of lines) {
                if (line.startsWith('data:')) {
                  try {
                    const payload: SsePayload = JSON.parse(line.slice(5).trim());
                    if (isTerminal(payload.status)) {
                      terminated = true;
                    }
                    onEvent(payload);
                  } catch {
                    // non-JSON SSE line (keep-alive comment) — skip
                  }
                }
              }

              pump();
            })
            .catch((err: Error) => {
              if (err.name !== 'AbortError') handleError(err, false);
            });
        }

        pump();
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') handleError(err, false);
      });
  }

  function handleError(err: Error, permanent: boolean) {
    if (closed || terminated) return;

    if (permanent || attempts >= maxRetries) {
      onError(err);
      return;
    }

    attempts++;
    const delay = baseDelay * Math.pow(2, attempts - 1) + Math.random() * 500;
    onRetry?.({ attempt: attempts, maxRetries, delay: Math.round(delay), error: err });
    setTimeout(connect, delay);
  }

  connect();

  return () => {
    closed = true;
    controller.abort();
  };
}
